import React, { useEffect, useState } from 'react';
import notify from '../../utils/notifier';
import LocationsService from '../../services/LocationsService';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Typography,
  Box,
  Grid
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PaginationComponent from './../PaginationComponent';
import DownloadIcon from '@mui/icons-material/Download';
import { IconButton } from '@mui/material';


const downloadCountries = async () => {
    let query = {};
    try {
      if(query._format === undefined || !query._format){
        query._format = 'CSV'
      }

      const response = await LocationsService.downloadCountriesData(query);
      console.log('Download response:', response.data);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(response.data);
      
      const timestamp = new Date().toLocaleString('en-GB').replace(/[/,: ]/g, '-');
      const fileName = `countries_${timestamp}.csv`;
      
      link.download = fileName; 
      link.click();
      setTimeout(() => {
        notify({
          type: 'success',
          message: 'File downloaded successfully.'
        });
      }, 50); 
    } catch (error) {
      console.error('Failed to download file:', error);
      notify({
        type: 'error',
        message: 'Something went wrong while downloading the file.'
      });
    }
};


const fetchCountries = async () => {
  let output = [];
    try {

      const response = await LocationsService.getCountriesData();
      output.data = []
      response.data.forEach(response => {
        output.push(response)
      })
      if (response.headers.hasOwnProperty('x-total-count')) {
        output['x-total-count'] = parseInt(response.headers['x-total-count']);
      }
    } catch (error) {
      notify({
        type: 'error',
        message: 'Internal Server Error'
      });
    }
    return output;
};

const columns = [
  { id: 'id', label: 'ID' },
  { id: 'name', label: 'Country Name' },
  { id: 'iso2', label: 'ISO2' },
  { id: 'iso3', label: 'ISO3' },
];

const StaticCountriesTable = () => {
  const [countries, setCountries] = useState([]);
  const [search, setSearch] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    fetchCountries().then(setCountries);
  }, []);

  useEffect(() => {
    setFiltered(
      countries.filter(
        c =>
          (c.name && c.name.toLowerCase().includes(search.toLowerCase())) ||
          (c.iso2 && c.iso2.toLowerCase().includes(search.toLowerCase())) ||
          (c.iso3 && c.iso3.toLowerCase().includes(search.toLowerCase())) || 
          (c.id && c.id.toLowerCase().includes(search.toLowerCase()))
      )
    );
    setPage(0); // Reset to first page when searching
  }, [search, countries]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate paginated data
  const paginatedData = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3, backgroundColor: '#F2F2F2', minHeight: 'calc(100vh - 144px)' }}>
      <Paper sx={{ p: 3, backgroundColor: '#FFFFFF' }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#298BC1' }}>
              Countries
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                variant="outlined"
                placeholder="Search by name, ISO2, ISO3, ID"
                value={search}
                onChange={e => setSearch(e.target.value)}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <IconButton
                onClick={downloadCountries}
                sx={{
                  backgroundColor: '#298BC1',
                  color: 'white',
                  '&:hover': { backgroundColor: '#2477a9' },
                  borderRadius: '6px',
                  p: 1.2
                }}
              >
                <DownloadIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        <TableContainer 
          component={Paper} 
          sx={{ 
            boxShadow: 'none', 
            border: '1px solid #e0e0e0',
            maxHeight: 'calc(100vh - 300px)', // Fixed height to prevent page scroll
            overflow: 'auto' // Enable table scrolling
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                {columns.map(col => (
                  <TableCell 
                    key={col.id} 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: '#333',
                      borderBottom: '2px solid #e0e0e0',
                      backgroundColor: '#f5f5f5' // Ensure header stays visible
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map(row => (
                <TableRow 
                  key={row.id}
                  sx={{ 
                    '&:hover': { backgroundColor: '#f8f9fa' },
                    '&:nth-of-type(odd)': { backgroundColor: '#fafafa' }
                  }}
                >
                  {columns.map(col => (
                    <TableCell 
                      key={col.id}
                      sx={{ 
                        borderBottom: '1px solid #e0e0e0',
                        color: '#333'
                      }}
                    >
                      {row[col.id]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {paginatedData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 4, color: '#666' }}>
                    No countries found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 2 }}>
          <PaginationComponent
            queryCount={filtered.length}
            page={page}
            handleChangePage={handleChangePage}
            rowsPerPage={rowsPerPage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default StaticCountriesTable; 